import { useState } from "react";
import { MessageCircle, ChevronRight, ArrowLeft, Heart, Reply, Star } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import UserAvatar from "./UserAvatar";

interface Comment {
  id: string;
  text: string;
  createdAt: string;
  authorName: string;
  authorAvatar?: string;
  reviewId: string;
  reviewRestaurantName: string;
  reviewRating: number;
  likes?: number;
}

interface CommentsManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CommentsManager({ isOpen, onClose }: CommentsManagerProps) {
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");

  // Fetch recent comments on user's reviews
  const { data: comments, isLoading } = useQuery<Comment[]>({
    queryKey: ['/api/user/comments'],
    enabled: isOpen
  });

  // Group comments by review
  const commentsByReview = comments?.reduce((acc, comment) => {
    if (!acc[comment.reviewId]) {
      acc[comment.reviewId] = {
        reviewId: comment.reviewId,
        restaurantName: comment.reviewRestaurantName,
        rating: comment.reviewRating,
        comments: []
      };
    }
    acc[comment.reviewId].comments.push(comment);
    return acc;
  }, {} as Record<string, { reviewId: string; restaurantName: string; rating: number; comments: Comment[] }>) || {};

  const reviewsWithComments = Object.values(commentsByReview);
  const selectedReview = selectedReviewId ? commentsByReview[selectedReviewId] : null;

  const handleReply = (commentId: string) => {
    // TODO: Implement reply functionality with API call
    console.log("Replying to comment:", commentId, "with text:", replyText);
    setReplyingTo(null);
    setReplyText("");
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="bottom" className="h-[85svh]">
        <SheetHeader className="mb-4">
          <div className="flex items-center gap-2">
            {selectedReviewId && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setSelectedReviewId(null);
                  setReplyingTo(null);
                  setReplyText("");
                }}
                data-testid="button-back-to-list"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <MessageCircle className="w-5 h-5 text-primary" />
            <SheetTitle>
              {selectedReview ? selectedReview.restaurantName : 'Comments & Messages'}
            </SheetTitle>
          </div>
        </SheetHeader>

        <ScrollArea className="h-[calc(85svh - 80px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              Loading comments...
            </div>
          ) : comments?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <MessageCircle className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="font-semibold text-lg mb-2">No comments yet</h3>
              <p className="text-muted-foreground text-sm max-w-sm">
                When people comment on your reviews, they'll appear here
              </p>
            </div>
          ) : selectedReview ? (
            /* Detailed view for single review */
            <div className="space-y-4 pb-4">
              {/* Review info card */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold">Your review</span>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.round(selectedReview.rating) }).map((_, i) => (
                        <Star key={i} className="w-3 h-3 fill-primary text-primary" />
                      ))}
                      <span className="text-sm text-muted-foreground ml-1">
                        {selectedReview.rating}/5
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {selectedReview.comments.length} {selectedReview.comments.length === 1 ? 'comment' : 'comments'}
                  </p>
                </CardContent>
              </Card>

              {/* All comments */}
              <div className="space-y-3">
                {selectedReview.comments.map((comment) => (
                  <Card key={comment.id}>
                    <CardContent className="p-4">
                      <div className="flex gap-3">
                        <UserAvatar
                          name={comment.authorName}
                          src={comment.authorAvatar}
                          size="sm"
                        />
                        <div className="flex-1 min-w-0 space-y-2">
                          <div>
                            <div className="flex items-baseline gap-2 mb-1">
                              <span className="font-medium text-sm">{comment.authorName}</span>
                              <span className="text-xs text-muted-foreground">
                                {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                              </span>
                            </div>
                            <p className="text-sm">{comment.text}</p>
                          </div>

                          {/* Comment actions */}
                          <div className="flex items-center gap-4">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-auto p-0 text-muted-foreground hover-elevate"
                              data-testid={`button-like-${comment.id}`}
                            >
                              <Heart className="w-3 h-3 mr-1" />
                              <span className="text-xs">{comment.likes || 0}</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-auto p-0 text-muted-foreground hover-elevate"
                              onClick={() => setReplyingTo(comment.id)}
                              data-testid={`button-reply-${comment.id}`}
                            >
                              <Reply className="w-3 h-3 mr-1" />
                              <span className="text-xs">Reply</span>
                            </Button>
                          </div>

                          {/* Reply input */}
                          {replyingTo === comment.id && (
                            <div className="space-y-2 pt-2">
                              <Textarea
                                placeholder={`Reply to ${comment.authorName}...`}
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                className="min-h-[80px]"
                                data-testid={`textarea-reply-${comment.id}`}
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleReply(comment.id)}
                                  disabled={!replyText.trim()}
                                  data-testid={`button-send-reply-${comment.id}`}
                                >
                                  Send Reply
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setReplyingTo(null);
                                    setReplyText("");
                                  }}
                                  data-testid={`button-cancel-reply-${comment.id}`}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            /* List view */
            <div className="space-y-4 pb-4">
              {reviewsWithComments.map((review) => (
                <Card key={review.reviewId} data-testid={`comment-group-${review.reviewId}`}>
                  <CardContent className="p-4">
                    {/* Review Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex-1">
                        <div className="font-semibold">{review.restaurantName}</div>
                        <div className="text-sm text-muted-foreground">
                          Your review • {review.rating}/10
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <MessageCircle className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {review.comments.length}
                        </span>
                      </div>
                    </div>

                    <Separator className="mb-3" />

                    {/* Comments */}
                    <div className="space-y-3">
                      {review.comments.slice(0, 3).map((comment) => (
                        <div 
                          key={comment.id} 
                          className="flex gap-3"
                          data-testid={`comment-${comment.id}`}
                        >
                          <UserAvatar
                            name={comment.authorName}
                            src={comment.authorAvatar}
                            size="sm"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2 mb-1">
                              <span className="font-medium text-sm" data-testid={`comment-author-${comment.id}`}>
                                {comment.authorName}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground" data-testid={`comment-text-${comment.id}`}>
                              {comment.text}
                            </p>
                          </div>
                        </div>
                      ))}

                      {review.comments.length > 3 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-primary"
                          onClick={() => setSelectedReviewId(review.reviewId)}
                          data-testid={`button-view-all-${review.reviewId}`}
                        >
                          View all {review.comments.length} comments
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      )}

                      {/* Always show a way to view details and reply */}
                      {review.comments.length <= 3 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-primary"
                          onClick={() => setSelectedReviewId(review.reviewId)}
                          data-testid={`button-reply-to-comments-${review.reviewId}`}
                        >
                          <Reply className="w-4 h-4 mr-1" />
                          Reply to comments
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
